/**
 * Fleet Booking Management API
 *
 * Fleet-portal (CLIENT user) booking endpoints.
 *
 * SCOPING: no call sends a customer id. The backend resolves the fleet customer
 * from the `customerUuid` JWT claim and scopes everything to it.
 *
 * PRIVACY: the calendar returns two lists — `myBookings` (full detail) and
 * `occupiedSlots` (date/time/duration only, no customer or vehicle data). Other
 * customers' details are never sent to the fleet portal, so the "busy" blocks
 * genuinely carry no identifying information.
 */

import axiosInstance from './axiosInstance';

// ============================================
// Calendar
// ============================================

/**
 * Load the fleet customer's calendar for a date range.
 *
 * @param {{ startDate: string, endDate: string }} params ISO dates (YYYY-MM-DD)
 * @returns {Promise<{ myBookings: Array, occupiedSlots: Array }>}
 */
export const fetchFleetCalendarApi = async ({ startDate, endDate }) => {
    const response = await axiosInstance.get('/fleet/bookings/calendar', {
        params: { startDate, endDate },
    });
    return response.data;
};

// ============================================
// Service categories
// ============================================

/**
 * The service categories this workshop offers — populates the booking form's
 * dropdown. Org-type aware server-side (a car wash returns its own categories).
 *
 * The `value` of each option is the enum name and is exactly what must be sent
 * back as a service type on create — the backend rejects anything else.
 *
 * @returns {Promise<Array<{ value: string, label: string, description?: string }>>}
 */
export const fetchFleetServiceCategoriesApi = async () => {
    const response = await axiosInstance.get('/fleet/bookings/service-categories');
    return response.data;
};

/**
 * The service packages this workshop offers — populates the booking form's
 * package dropdown. Only active profiles are returned.
 *
 * @returns {Promise<Array<{ uuid, name, make?, model?, vehicleYear?, mileageKm? }>>}
 */
export const fetchFleetServiceProfilesApi = async () => {
    const response = await axiosInstance.get('/fleet/bookings/service-profiles');
    return response.data;
};

// ============================================
// Create
// ============================================

/**
 * Book one of the fleet customer's own vehicles in for a service.
 *
 * Supply EITHER `serviceProfileUuid` (a package) OR `serviceTypes` (individual
 * services) — never both. The backend rejects an ambiguous or empty request.
 *
 * @param {{
 *   vehicleUuid: string,
 *   scheduledDate: string,
 *   scheduledTime?: string,
 *   serviceProfileUuid?: string,
 *   serviceTypes?: Array<{ type: string, durationMinutes?: number, description?: string }>,
 *   description?: string,
 *   estimatedDurationMinutes?: number
 * }} payload
 */
export const createFleetBookingApi = async (payload) => {
    const response = await axiosInstance.post('/fleet/bookings', payload);
    return response.data;
};

// ============================================
// Cancel
// ============================================

/**
 * Cancel one of the fleet customer's own bookings.
 * Only allowed while the booking is still SCHEDULED — the backend rejects
 * cancellation once work has started.
 */
export const cancelFleetBookingApi = async (bookingUuid) => {
    const response = await axiosInstance.delete(`/fleet/bookings/${bookingUuid}`);
    return response.data;
};

// ============================================
// Aggregate export
// ============================================

const fleetBookingMgtApi = {
    fetchFleetCalendarApi,
    fetchFleetServiceCategoriesApi,
    fetchFleetServiceProfilesApi,
    createFleetBookingApi,
    cancelFleetBookingApi,
};

export default fleetBookingMgtApi;